import { supabase } from "./supabase";
import { Session, UserResponse, User } from "@supabase/supabase-js";

export const signInWithPassword = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const signUp = async (
  email: string,
  password: string,
  options?: {
    data?: Record<string, unknown>;
  }
) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: options || {},
    });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
    return { error: null };
  } catch (error) {
    return { error };
  }
};

// Helper to get user from localStorage directly (bypasses network request)
const getUserFromStorage = (): { user: User | null; error: string | null } => {
  try {
    // Try different possible storage keys that Supabase might use
    const possibleKeys = [
      "supabase.auth.token",
      `sb-${import.meta.env.VITE_SUPABASE_URL?.split("//")[1]?.split(".")[0]}-auth-token`,
      "supabase.auth.session",
    ];

    let sessionData: Record<string, unknown> | null = null;

    for (const key of possibleKeys) {
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          sessionData = JSON.parse(stored) as Record<string, unknown>;
          break;
        } catch {
          // Failed to parse, try next key
        }
      }
    }

    if (!sessionData) {
      return { user: null, error: "No session in storage" };
    }

    // Try different possible structures
    let user: User | null = null;

    // Structure 1: { currentSession: { user: ... } }
    const currentSession = sessionData.currentSession as
      | Record<string, unknown>
      | undefined;
    if (currentSession?.user) {
      user = currentSession.user as User;
    }
    // Structure 2: { user: ... } - direct user property
    else if (sessionData.user) {
      user = sessionData.user as User;
    }

    if (user) {
      return { user, error: null };
    }

    return { user: null, error: "User not found in storage" };
  } catch {
    return { user: null, error: "Failed to read storage" };
  }
};

export const getCurrentUser = async () => {
  try {
    // Start auto-refresh if tab is visible (helps with tab switching)
    if (
      typeof window !== "undefined" &&
      document.visibilityState === "visible"
    ) {
      try {
        supabase.auth.startAutoRefresh();
      } catch {
        // Ignore auto-refresh errors
      }
    }

    // First, try to get from localStorage (fast, no network)
    const storageUser = getUserFromStorage();
    if (storageUser.user && !storageUser.error) {
      return { user: storageUser.user, error: null };
    }

    // If localStorage doesn't have user, try network request with timeout
    const getUserPromise = supabase.auth.getUser();
    let timeoutId: NodeJS.Timeout | null = null;
    const timeoutDuration = 5000;

    const timeoutPromise = new Promise<UserResponse>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`getUser() timeout after ${timeoutDuration}ms`));
      }, timeoutDuration);
    });

    try {
      const result = await Promise.race([getUserPromise, timeoutPromise]);
      if (timeoutId) clearTimeout(timeoutId);

      const { data, error } = result;

      if (error) {
        throw error;
      }

      const user = data?.user;
      return { user, error: null };
    } catch (raceError) {
      if (timeoutId) clearTimeout(timeoutId);

      // If timeout, try localStorage fallback again (in case it was updated)
      if (raceError instanceof Error && raceError.message.includes("timeout")) {
        const fallbackUser = getUserFromStorage();
        if (fallbackUser.user && !fallbackUser.error) {
          return { user: fallbackUser.user, error: null };
        }
      }

      throw raceError;
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    return { user: null, error: errorMessage };
  }
};

export const getCurrentSession = async () => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error) {
      throw error;
    }
    return { session, error: null };
  } catch (error) {
    return { session: null, error };
  }
};

export const getUserWithToken = async (token: string) => {
  try {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser(token);
    if (error) {
      throw error;
    }
    return { user, error: null };
  } catch (error) {
    return { user: null, error };
  }
};

export const resetPasswordForEmail = async (
  email: string,
  redirectTo?: string
) => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectTo || `${window.location.origin}/reset-password`,
    });
    if (error) {
      throw error;
    }
    return { error: null };
  } catch (error) {
    return { error };
  }
};

export const updateUserPassword = async (password: string) => {
  try {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      throw error;
    }
    return { error: null };
  } catch (error) {
    return { error };
  }
};

export const onAuthStateChange = (
  callback: (event: string, session: Session | null) => void
) => {
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange(callback);
  return subscription;
};
